import { useState, useEffect, useRef } from 'react';
import api from '../api';
import {
  Image as ImageIcon,
  Plus,
  Edit2,
  Trash2,
  X,
  ArrowUp,
  ArrowDown,
  Eye,
  EyeOff,
  Calendar,
  Clock,
  Scale,
  Upload,
  Save,
  Target,
} from 'lucide-react';

// Game-affinity targeting options (mirrors the backend enum).
const AFFINITY_OPTIONS = [
  { value: 'all', label: 'All players' },
  { value: 'poker', label: 'Poker players' },
  { value: 'cal_games', label: 'Cal game players' },
];
const AFFINITY_LABEL = Object.fromEntries(AFFINITY_OPTIONS.map((o) => [o.value, o.label]));

// Convert a File to a base64 data URL (kept full so callers can also extract mime).
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Render a Date as a value suitable for <input type="datetime-local"> (no TZ).
function toLocalInputValue(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function adIsLive(ad) {
  if (!ad.isActive) return false;
  const now = Date.now();
  if (ad.startAt && new Date(ad.startAt).getTime() > now) return false;
  if (ad.endAt && new Date(ad.endAt).getTime() < now) return false;
  return true;
}

function emptyForm() {
  return {
    title: '',
    imageBase64: '',
    imageMimeType: '',
    previewUrl: '',
    displayOrder: 0,
    weight: 1,
    startAt: '',
    endAt: '',
    minDisplaySeconds: 0,
    isActive: true,
    gameAffinity: 'all',
  };
}

export default function AdManager() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // ad object being edited (or 'new')
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const fetchAds = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/ads');
      setAds(res.data.ads || []);
    } catch (err) {
      console.error('Error fetching ads:', err);
      setError('Failed to load ads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAds(); }, []);

  function openNew() {
    setForm(emptyForm());
    setEditing('new');
    setError('');
  }

  function openEdit(ad) {
    setForm({
      title: ad.title || '',
      imageBase64: '',
      imageMimeType: '',
      previewUrl: ad.imageUrl || '',
      displayOrder: ad.displayOrder ?? 0,
      weight: ad.weight ?? 1,
      startAt: toLocalInputValue(ad.startAt),
      endAt: toLocalInputValue(ad.endAt),
      minDisplaySeconds: ad.minDisplaySeconds ?? 0,
      isActive: ad.isActive !== false,
      gameAffinity: ad.gameAffinity || 'all',
    });
    setEditing(ad);
    setError('');
  }

  function closeForm() {
    setEditing(null);
    setForm(emptyForm());
    setError('');
  }

  async function onFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please pick an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB');
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    setForm((f) => ({
      ...f,
      imageBase64: dataUrl, // backend strips the data-URL prefix
      imageMimeType: file.type,
      previewUrl: dataUrl,
    }));
    setError('');
  }

  async function handleSave() {
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    if (editing === 'new' && !form.imageBase64) {
      setError('Image is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: form.title.trim(),
        displayOrder: Number(form.displayOrder) || 0,
        weight: Number(form.weight) || 0,
        minDisplaySeconds: Number(form.minDisplaySeconds) || 0,
        isActive: Boolean(form.isActive),
        gameAffinity: form.gameAffinity || 'all',
        // datetime-local sends "YYYY-MM-DDTHH:mm" in *local* time; let
        // the browser convert via Date so the backend gets ISO with TZ.
        startAt: form.startAt ? new Date(form.startAt).toISOString() : null,
        endAt: form.endAt ? new Date(form.endAt).toISOString() : null,
      };
      if (form.imageBase64) {
        payload.imageBase64 = form.imageBase64;
        payload.imageMimeType = form.imageMimeType;
      }
      if (editing === 'new') {
        await api.post('/admin/ads', payload);
      } else {
        await api.put(`/admin/ads/${editing.id}`, payload);
      }
      closeForm();
      await fetchAds();
    } catch (err) {
      console.error('Save failed:', err);
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(ad) {
    try {
      await api.put(`/admin/ads/${ad.id}`, { isActive: !ad.isActive });
      fetchAds();
    } catch (err) {
      console.error('Toggle failed:', err);
    }
  }

  async function handleDelete(ad) {
    if (!window.confirm(`Delete ad "${ad.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/ads/${ad.id}`);
      fetchAds();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  }

  async function handleMove(ad, direction) {
    const idx = ads.findIndex((a) => a.id === ad.id);
    const swapWith = direction === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= ads.length) return;
    const a = ads[idx];
    const b = ads[swapWith];
    try {
      await api.post('/admin/ads/reorder', {
        items: [
          { id: a.id, displayOrder: b.displayOrder },
          { id: b.id, displayOrder: a.displayOrder },
        ],
      });
      fetchAds();
    } catch (err) {
      console.error('Reorder failed:', err);
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ImageIcon className="w-7 h-7 text-amber-500" />
            Ad Manager
          </h1>
          <p className="text-gray-400 mt-1 text-sm">
            Full-screen ads shown to players while the app is loading. Higher weights show more often; the close button enables once the app is ready (or after a brief timeout).
          </p>
        </div>
        <button
          onClick={openNew}
          className="bg-amber-500 hover:bg-amber-600 text-gray-900 font-semibold px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Ad
        </button>
      </div>

      {error && !editing && (
        <div className="mb-4 p-3 rounded bg-red-900/50 border border-red-700 text-red-200 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-400">Loading ads…</div>
      ) : ads.length === 0 ? (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-12 text-center text-gray-400">
          <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="font-medium text-gray-200">No ads yet</p>
          <p className="text-sm mt-1">Click "New Ad" to upload your first full-screen ad.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {ads.map((ad, idx) => {
            const live = adIsLive(ad);
            return (
              <div
                key={ad.id}
                className="bg-gray-800 border border-gray-700 rounded-lg p-4 flex gap-4 items-center"
              >
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => handleMove(ad, 'up')}
                    disabled={idx === 0}
                    className="text-gray-400 hover:text-white disabled:opacity-30"
                    title="Move up"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleMove(ad, 'down')}
                    disabled={idx === ads.length - 1}
                    className="text-gray-400 hover:text-white disabled:opacity-30"
                    title="Move down"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>

                {ad.imageUrl ? (
                  <img
                    src={ad.imageUrl}
                    alt={ad.title}
                    className="w-24 h-32 object-cover rounded border border-gray-700 bg-gray-900"
                  />
                ) : (
                  <div className="w-24 h-32 rounded border border-gray-700 bg-gray-900 flex items-center justify-center text-gray-600">
                    <ImageIcon className="w-6 h-6" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-semibold truncate">{ad.title}</h3>
                    {live ? (
                      <span className="text-xs bg-emerald-900 text-emerald-300 px-2 py-0.5 rounded-full border border-emerald-700">
                        LIVE
                      </span>
                    ) : ad.isActive ? (
                      <span className="text-xs bg-amber-900 text-amber-300 px-2 py-0.5 rounded-full border border-amber-700">
                        SCHEDULED
                      </span>
                    ) : (
                      <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full border border-gray-600">
                        OFF
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 mt-2 flex flex-wrap gap-x-4 gap-y-1">
                    <span className="flex items-center gap-1"><Scale className="w-3 h-3" /> Weight {ad.weight}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Min {ad.minDisplaySeconds}s</span>
                    <span>Order {ad.displayOrder}</span>
                    {ad.gameAffinity && ad.gameAffinity !== 'all' && (
                      <span className="flex items-center gap-1 text-amber-300">
                        <Target className="w-3 h-3" /> {AFFINITY_LABEL[ad.gameAffinity] || ad.gameAffinity}
                      </span>
                    )}
                    {ad.startAt && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Starts {new Date(ad.startAt).toLocaleString()}
                      </span>
                    )}
                    {ad.endAt && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Ends {new Date(ad.endAt).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleActive(ad)}
                    className="p-2 rounded bg-gray-700 hover:bg-gray-600 text-gray-200"
                    title={ad.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {ad.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => openEdit(ad)}
                    className="p-2 rounded bg-gray-700 hover:bg-gray-600 text-gray-200"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(ad)}
                    className="p-2 rounded bg-red-900/50 hover:bg-red-900 text-red-200 border border-red-800"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/70 z-30 flex items-center justify-center p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h2 className="text-lg font-semibold text-white">
                {editing === 'new' ? 'New Ad' : 'Edit Ad'}
              </h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {error && (
                <div className="p-3 rounded bg-red-900/50 border border-red-700 text-red-200 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm text-gray-300 mb-1">Title (internal label)</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  maxLength={120}
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                  placeholder="e.g. Spring Tournament Promo"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-1">Image (full-screen, recommend 1080×1920)</label>
                <div className="flex items-start gap-3">
                  <div className="w-32 h-56 bg-gray-900 border border-gray-700 rounded flex items-center justify-center overflow-hidden">
                    {form.previewUrl ? (
                      <img src={form.previewUrl} alt="preview" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-gray-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={onFileChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      {editing === 'new' ? 'Choose image' : 'Replace image'}
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      Up to 5MB. JPG, PNG, or WebP. Tall portrait artwork looks best on phone screens.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Display order</label>
                  <input
                    type="number"
                    min={0}
                    value={form.displayOrder}
                    onChange={(e) => setForm({ ...form, displayOrder: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lower numbers come first in rotation.</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Weight</label>
                  <input
                    type="number"
                    min={0}
                    max={1000}
                    value={form.weight}
                    onChange={(e) => setForm({ ...form, weight: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">Higher = shown more often. 0 = paused.</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Start (optional)</label>
                  <input
                    type="datetime-local"
                    value={form.startAt}
                    onChange={(e) => setForm({ ...form, startAt: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">End (optional)</label>
                  <input
                    type="datetime-local"
                    value={form.endAt}
                    onChange={(e) => setForm({ ...form, endAt: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-1">Minimum display time (seconds)</label>
                <input
                  type="number"
                  min={0}
                  max={60}
                  value={form.minDisplaySeconds}
                  onChange={(e) => setForm({ ...form, minDisplaySeconds: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Even if the app loads fast, the close button stays disabled for this many seconds.
                </p>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-1 flex items-center gap-1">
                  <Target className="w-4 h-4 text-amber-400" /> Game affinity (who sees this ad)
                </label>
                <select
                  value={form.gameAffinity}
                  onChange={(e) => setForm({ ...form, gameAffinity: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                >
                  {AFFINITY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Poker / Cal targets players who logged that game type in the last 90 days
                  (a player who plays both sees both). "All players" shows to everyone.
                </p>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  id="active-toggle"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="active-toggle" className="text-sm text-gray-200">
                  Active (eligible for display)
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-gray-700">
              <button
                onClick={closeForm}
                className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-600 text-gray-900 font-semibold flex items-center gap-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
